// =============================================================================
// FILE: packages/renderer/src/stores/stealth.store.ts
// PURPOSE: "Stealth Workspace" (Privacy Cloak) state. When active, a full-screen
//   decoy covers the entire RCD² UI so shoulder-surfers / nosy colleagues can't
//   see proprietary designs. Exit requires a PIN (set on first use) OR the
//   operator's login email as a fallback. Persisted to localStorage so if the
//   app is closed while cloaked, it reopens LOCKED to the decoy until unlocked.
// =============================================================================

import { create } from 'zustand';

const LS_KEY = 'rcd2.stealth.v1';
export type DecoyKind = 'word' | 'pdf' | 'sheet';
const DECOYS: DecoyKind[] = ['word', 'pdf', 'sheet'];

interface Persisted { active: boolean; pin: string | null; decoy: DecoyKind; }

function load(): Persisted {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (raw) {
      const p = JSON.parse(raw) as Partial<Persisted>;
      return {
        active: !!p.active,
        pin: typeof p.pin === 'string' ? p.pin : null,
        decoy: (DECOYS as string[]).includes(p.decoy as string) ? (p.decoy as DecoyKind) : 'word',
      };
    }
  } catch { /* */ }
  return { active: false, pin: null, decoy: 'word' };
}

function save(s: Persisted): void { try { localStorage.setItem(LS_KEY, JSON.stringify(s)); } catch { /* */ } }

function randDecoy(exclude?: DecoyKind): DecoyKind {
  const pool = DECOYS.filter((d) => d !== exclude);
  return pool[Math.floor(Math.random() * pool.length)] ?? 'word';
}

export function isValidPin(pin: string): boolean { return /^\d{4,6}$/.test(pin.trim()); }

interface StealthState {
  active: boolean;
  pin: string | null;
  hasPin: boolean;
  decoy: DecoyKind;
  setPin: (pin: string) => void;
  activate: () => void;                                   // shuffle a decoy + lock
  shuffle: () => void;                                    // change the decoy look
  tryUnlock: (value: string, email?: string | null) => boolean;
}

const init = load();

export const useStealthStore = create<StealthState>((set, get) => ({
  active: init.active,
  pin: init.pin,
  hasPin: !!init.pin,
  decoy: init.decoy,

  setPin: (pin) => { save({ active: get().active, pin, decoy: get().decoy }); set({ pin, hasPin: true }); },

  activate: () => { const decoy = randDecoy(); save({ active: true, pin: get().pin, decoy }); set({ active: true, decoy }); },

  shuffle: () => { const decoy = randDecoy(get().decoy); save({ active: get().active, pin: get().pin, decoy }); set({ decoy }); },

  tryUnlock: (value, email) => {
    const v = value.trim();
    const pin = get().pin;
    const pinOk = !!pin && v === pin;
    const emailOk = !!email && v.toLowerCase() === email.toLowerCase();
    if (pinOk || emailOk) { save({ active: false, pin, decoy: get().decoy }); set({ active: false }); return true; }
    return false;
  },
}));
